import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Reveal, SectionHeading } from "@/components/Shared";

const STEPS = [
  {
    num: "01",
    phase: "Synthesis",
    title: "Hiring-Signal Targeting",
    copy: "We build lists from live hiring intent — open tech roles, funding rounds, and team expansion — then map the decision-makers who own the requisition.",
  },
  {
    num: "02",
    phase: "Calibration",
    title: "LinkedIn + Email Sequences",
    copy: "Multi-channel outreach that reads like a human wrote it. Insight-led messaging that positions your firm as a talent partner, not another agency spamming CVs.",
  },
  {
    num: "03",
    phase: "Operation",
    title: "Conversation Handling",
    copy: "We manage replies, objections, and follow-ups until a meeting is confirmed. You spend your time placing candidates, not chasing prospects.",
  },
];

const RAMP = [
  { month: "Month 1", meetings: 3 },
  { month: "Month 2", meetings: 6 },
  { month: "Month 3", meetings: 10 },
  { month: "Ongoing", meetings: 10 },
];

const MeetingsGraph = () => (
  <div className="rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] p-6 sm:p-8" data-testid="meetings-graph">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <p className="font-display text-lg font-bold tracking-tight text-[#3B0764]">
        The trajectory we build toward
      </p>
      <p className="text-xs text-[#6B5CA5]">Illustrative ramp — every market is different</p>
    </div>
    <div className="mt-6 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={RAMP} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id="meetingsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6B5CA5" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 12, fill: "#6B5CA5" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip
            cursor={{ stroke: "#C084FC", strokeDasharray: "4 4" }}
            contentStyle={{ borderRadius: 12, border: "1px solid #E9E4F0", fontSize: 13 }}
            formatter={(v) => [`${v} meetings`, "Booked"]}
          />
          <ReferenceLine
            y={10}
            stroke="#7C3AED"
            strokeDasharray="5 5"
            label={{ value: "Goal: 10+", position: "insideTopRight", fontSize: 12, fill: "#5B21B6", fontWeight: 600 }}
          />
          <Area type="monotone" dataKey="meetings" stroke="#7C3AED" strokeWidth={2.5} fill="url(#meetingsFill)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export const HowItWorks = () => (
  <section id="how" className="py-20 sm:py-28" data-testid="how-it-works-section">
    <div className="max-w-6xl mx-auto px-6">
      <SectionHeading
        eyebrow="How it works"
        title="A systematic approach to booking 10+ meetings a month."
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.1}>
            <div
              data-testid={`how-step-${s.num}`}
              className="h-full rounded-3xl border border-[#E9E4F0] bg-[#EDEDF1] p-8 transition-colors duration-300 hover:border-violet-300"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-sm font-bold text-violet-700">{s.num}</span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6B5CA5]">{s.phase}</span>
              </div>
              <h3 className="font-display mt-6 text-xl font-semibold tracking-tight text-[#3B0764]">{s.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-[#6B5CA5]">{s.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15} className="mt-6">
        <MeetingsGraph />
      </Reveal>
    </div>
  </section>
);
